<!-- ============================================================
     GATE PASS SYSTEM FOR STUDENTS
     ============================================================ -->
<div class="gp-page">

  <!-- Toast -->
  @if (saveSuccess()) {
    <div class="gp-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ saveSuccess() }}</strong>
    </div>
  }

  <!-- Approve/Reject Modal -->
  @if (approveRejectMode()) {
    <div class="gp-overlay" (click)="approveRejectMode.set(null)">
      <div class="gp-modal" (click)="$event.stopPropagation()">
        <div class="gmh {{ approveRejectMode() === 'approve' ? 'gmh-approve' : 'gmh-reject' }}">
          <div class="gmh-icon">
            <i class="bi {{ approveRejectMode() === 'approve' ? 'bi-check-circle-fill' : 'bi-x-circle-fill' }}"></i>
          </div>
          <div>
            <h5 class="gmh-title">{{ approveRejectMode() === 'approve' ? 'Approve Gate Pass?' : 'Reject Gate Pass?' }}</h5>
            <p class="gmh-sub">Pass #{{ passes().find(p => p.id === approveRejectId())?.pass_no }}</p>
          </div>
          <button class="gmh-close" (click)="approveRejectMode.set(null)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="gmb p-4">
          @if (approveRejectMode() === 'reject') {
            <label class="form-label fw-semibold">Rejection Reason <span class="req">*</span></label>
            <textarea class="form-control" [(ngModel)]="rejectReason" rows="3" placeholder="Reason for rejecting this gate pass…"></textarea>
            @if (!rejectReason.trim()) { <div class="text-danger small mt-1">Reason is required to reject.</div> }
          } @else {
            <p class="text-muted small mb-0">This will approve the gate pass and make it valid for use. The student will be allowed to exit/enter at the gate.</p>
          }
        </div>
        <div class="gmf">
          <button class="btn btn-outline-secondary px-4" (click)="approveRejectMode.set(null)">Cancel</button>
          @if (approveRejectMode() === 'approve') {
            <button class="btn btn-success px-5" (click)="confirmApprove()"><i class="bi bi-check-circle-fill me-2"></i>Approve</button>
          } @else {
            <button class="btn btn-danger px-5" (click)="confirmReject()" [disabled]="!rejectReason.trim()"><i class="bi bi-x-circle-fill me-2"></i>Reject</button>
          }
        </div>
      </div>
    </div>
  }

  <!-- Gate Scan Modal -->
  @if (showScanModal() && scanningPass()) {
    @let sp = scanningPass()!;
    <div class="gp-overlay" (click)="showScanModal.set(false)">
      <div class="gp-modal" (click)="$event.stopPropagation()">
        <div class="gmh gmh-scan">
          <div class="gmh-icon"><i class="bi bi-qr-code-scan"></i></div>
          <div>
            <h5 class="gmh-title">Gate Scan</h5>
            <p class="gmh-sub">{{ sp.pass_no }} · {{ sp.student_name }}</p>
          </div>
          <button class="gmh-close" (click)="showScanModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="gmb p-4">
          <div class="scan-student-info">
            <div class="ssi-avatar" [style.background]="'#1a3a6b'">{{ getInitials(sp.student_name) }}</div>
            <div>
              <div class="fw-bold">{{ sp.student_name }}</div>
              <div class="small text-muted">{{ dataService.getClassName(sp.class_name) }}-{{ sp.section }} · Roll: {{ sp.roll_no }}</div>
              <div class="small text-muted">Pass: {{ sp.pass_no }} · {{ sp.pass_type }}</div>
              <div class="small text-muted">Approved Out: {{ formatTimeStr(sp.out_time) }}{{ sp.in_time ? ' | Return: ' + formatTimeStr(sp.in_time) : '' }}</div>
            </div>
            <span class="{{ getStatusClass(sp.pass_status) }} ms-auto">{{ sp.pass_status }}</span>
          </div>
          <label class="form-label fw-semibold mt-3">Gate Remark (optional)</label>
          <input type="text" class="form-control" [(ngModel)]="scanRemark" maxlength="100" placeholder="e.g. Verified ID">
        </div>
        <div class="gmf gap-3">
          <button class="btn btn-outline-secondary px-3" (click)="showScanModal.set(false)">Cancel</button>
          @if (!sp.actual_out_time) {
            <button class="btn btn-warning flex-fill py-2 fw-bold" (click)="markOut()">
              <i class="bi bi-arrow-right-circle-fill me-2"></i>Mark EXIT (OUT)
            </button>
          } @else if (!sp.actual_in_time) {
            <button class="btn btn-success flex-fill py-2 fw-bold" (click)="markIn()">
              <i class="bi bi-arrow-left-circle-fill me-2"></i>Mark ENTRY (IN)
            </button>
          } @else {
            <span class="text-success fw-semibold"><i class="bi bi-check2-all me-1"></i>Both gates completed</span>
          }
        </div>
      </div>
    </div>
  }

  <!-- Pass View Modal (Printable) -->
  @if (showPassModal() && viewingPass()) {
    @let vp = viewingPass()!;
    <div class="gp-overlay" (click)="showPassModal.set(false)">
      <div class="pass-card-modal" (click)="$event.stopPropagation()">
        <div class="pcm-header">
          <div class="pcm-school-row">
            <div class="pcm-logo"><i class="bi bi-mortarboard-fill"></i></div>
            <div>
              <div class="pcm-school-name">EduSmart International School</div>
              <div class="pcm-title">STUDENT GATE PASS</div>
            </div>
          </div>
          <button class="gmh-close no-print" (click)="showPassModal.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="pcm-body">
          <div class="pcm-pass-no-row">
            <span class="pcm-pass-no">{{ vp.pass_no }}</span>
            <span class="{{ getStatusClass(vp.pass_status) }}">{{ vp.pass_status }}</span>
          </div>
          <div class="pcm-student-row">
            <div class="pcm-avatar">{{ getInitials(vp.student_name) }}</div>
            <div>
              <div class="fw-bold fs-6">{{ vp.student_name }}</div>
              <div class="small text-muted">{{ dataService.getClassName(vp.class_name) }} {{ vp.section }} · Roll: {{ vp.roll_no }}</div>
              <div class="small text-muted">ID: {{ vp.student_id }}</div>
            </div>
            <div class="{{ getTypeClass(vp.pass_type) }} type-badge-lg ms-auto">
              <i class="bi {{ getTypeIcon(vp.pass_type) }} me-1"></i>{{ vp.pass_type }}
            </div>
          </div>
          <div class="pcm-info-grid">
            <div class="pig-item"><span>Date</span><span>{{ formatDate(vp.reg_date) }}</span></div>
            <div class="pig-item"><span>Out Time</span><span class="fw-semibold">{{ formatTimeStr(vp.out_time) }}</span></div>
            @if (vp.in_time) { <div class="pig-item"><span>Return By</span><span class="fw-semibold">{{ formatTimeStr(vp.in_time) }}</span></div> }
            <div class="pig-item"><span>Reason</span><span>{{ vp.reason }}</span></div>
            @if (vp.contact_person) { <div class="pig-item"><span>Taken By</span><span>{{ vp.contact_person }}</span></div> }
            @if (vp.contact_mobile) { <div class="pig-item"><span>Contact</span><span>{{ vp.contact_mobile }}</span></div> }
            <div class="pig-item"><span>Requested By</span><span class="text-capitalize">{{ vp.requested_by }}</span></div>
            @if (vp.approved_by) { <div class="pig-item"><span>Approved By</span><span>{{ vp.approved_by }}</span></div> }
            @if (vp.approved_at) { <div class="pig-item"><span>Approved At</span><span>{{ formatDateTime(vp.approved_at) }}</span></div> }
          </div>

          @if (vp.actual_out_time || vp.actual_in_time) {
            <div class="gate-timestamps">
              <div class="gt-header"><i class="bi bi-clock-history me-2"></i>Gate Timestamps</div>
              <div class="gt-row">
                @if (vp.actual_out_time) {
                  <div class="gt-item out"><i class="bi bi-arrow-right-circle-fill"></i><div><div class="gt-label">Exited</div><div class="gt-time">{{ formatDateTime(vp.actual_out_time) }}</div></div></div>
                }
                @if (vp.actual_in_time) {
                  <div class="gt-item in"><i class="bi bi-arrow-left-circle-fill"></i><div><div class="gt-label">Returned</div><div class="gt-time">{{ formatDateTime(vp.actual_in_time) }}</div></div></div>
                }
                @if (vp.actual_out_time && vp.actual_in_time) {
                  <div class="gt-duration"><i class="bi bi-stopwatch me-1"></i>{{ calcDuration(vp.actual_out_time, vp.actual_in_time) }} out</div>
                }
              </div>
            </div>
          }

          @if (vp.rejected_reason) {
            <div class="rejected-reason"><i class="bi bi-exclamation-triangle-fill me-2 text-danger"></i><strong>Rejected:</strong> {{ vp.rejected_reason }}</div>
          }

          <div class="pcm-sig-row">
            <div class="pcm-sig"><div class="sig-line"></div><div class="sig-label">Parent / Guardian</div></div>
            <div class="pcm-sig"><div class="sig-line"></div><div class="sig-label">Class Teacher</div></div>
            <div class="pcm-sig"><div class="sig-line"></div><div class="sig-label">Principal</div></div>
          </div>
        </div>
        <div class="pcm-footer no-print">
          <div class="small text-muted">
            <i class="bi bi-info-circle me-1"></i>
            This pass is valid only for the mentioned date and time.
          </div>
          <button class="btn gp-btn-primary btn-sm" (click)="printPass()"><i class="bi bi-printer-fill me-1"></i>Print Pass</button>
        </div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="gp-ph mb-3">
    <div>
      <h1 class="gp-title"><i class="bi bi-pass-fill me-2 text-primary" style="font-size:1.1rem;"></i>Gate Pass System</h1>
      <p class="gp-subtitle">Issue · Approve · Track student entry & exit</p>
    </div>
    <!-- <button class="btn gp-btn-primary btn-sm" (click)="openIssueForm()">
      <i class="bi bi-plus-circle-fill me-1"></i>Issue Gate Pass
    </button> -->

    <!-- TABS -->
    <div class="gp-tabs">
      @for (t of [{k:'overview',l:'Overview',ic:'bi-grid-fill'},{k:'issue',l:'Issue Pass',ic:'bi-plus-circle-fill'},{k:'requests',l:'Requests',ic:'bi-list-check'},{k:'active',l:'Active',ic:'bi-record-circle-fill'},{k:'log',l:'Gate Log',ic:'bi-journal-text'}]; track t.k) {
        <button class="gptab" [class.active]="activeTab() === t.k" (click)="activeTab.set($any(t.k))">
          <i class="bi {{ t.ic }} me-1"></i>{{ t.l }}
          @if (t.k === 'requests' && stats().pending > 0) { <span class="tab-badge">{{ stats().pending }}</span> }
          @if (t.k === 'active'   && stats().active > 0)  { <span class="tab-badge tab-badge-green">{{ stats().active }}</span> }
        </button>
      }
    </div>
  </div>

  <!-- Stats Row -->
  <div class="gp-stats mb-3">
    <div class="gpst-item gpst-pending"  [class.gpst-alert]="stats().pending > 0">
      <div class="gpst-icon"><i class="bi bi-hourglass-split"></i></div>
      <div class="gpst-val">{{ stats().pending }}</div><div class="gpst-label">Pending</div>
    </div>
    <div class="gpst-item gpst-active" [class.gpst-alert]="stats().outNow > 0">
      <div class="gpst-icon"><i class="bi bi-record-circle-fill"></i></div>
      <div class="gpst-val">{{ stats().active }}</div><div class="gpst-label">Active (Out)</div>
    </div>
    <div class="gpst-item gpst-today">
      <div class="gpst-icon"><i class="bi bi-calendar-day-fill"></i></div>
      <div class="gpst-val">{{ stats().today }}</div><div class="gpst-label">Today's Passes</div>
    </div>
    <div class="gpst-item gpst-done">
      <div class="gpst-icon"><i class="bi bi-check2-all"></i></div>
      <div class="gpst-val">{{ stats().completed }}</div><div class="gpst-label">Completed</div>
    </div>
    <div class="gpst-item gpst-total">
      <div class="gpst-icon"><i class="bi bi-collection-fill"></i></div>
      <div class="gpst-val">{{ stats().total }}</div><div class="gpst-label">Total</div>
    </div>
  </div>

  <!-- ===================== OVERVIEW ===================== -->
  @if (activeTab() === 'overview') {
    <div class="row g-4">
      <!-- Today's Timeline -->
      <div class="col-lg-7">
        <div class="gp-card h-100">
          <div class="gpc-header"><i class="bi bi-clock-history me-2"></i>Today's Gate Pass Activity</div>
          @if (todayTimeline().length === 0) {
            <div class="empty-state"><i class="bi bi-pass"></i><p>No gate passes today</p></div>
          } @else {
            <div class="gpc-body p-0">
              @for (p of todayTimeline(); track p.id) {
                <div class="timeline-row" [class.tl-overdue]="isOverdue(p)">
                  <div class="tl-type-icon {{ getTypeClass(p.pass_type) }}">
                    <i class="bi {{ getTypeIcon(p.pass_type) }}"></i>
                  </div>
                  <div class="tl-content">
                    <div class="tl-student">{{ p.student_name }} <span class="tl-class">{{ dataService.getClassName(p.class_name) }}-{{ p.section }}</span></div>
                    <div class="tl-reason">{{ p.reason }}</div>
                    <div class="tl-meta">
                      <i class="bi bi-clock me-1"></i>Out: {{ formatTimeStr(p.out_time) }}
                      @if (p.in_time) { &nbsp;· Return: {{ formatTimeStr(p.in_time) }} }
                      @if (isOverdue(p)) { <span class="overdue-tag ms-2"><i class="bi bi-exclamation-triangle-fill me-1"></i>OVERDUE</span> }
                    </div>
                  </div>
                  <div class="tl-right">
                    <span class="{{ getStatusClass(p.pass_status) }}">{{ p.pass_status }}</span>
                    <div class="tl-actions mt-1">
                      @if (p.pass_status === 'Pending') {
                        <button class="tla-btn approve" (click)="openApprove(p)" title="Approve"><i class="bi bi-check-lg"></i></button>
                        <button class="tla-btn reject" (click)="openReject(p)"  title="Reject"><i class="bi bi-x-lg"></i></button>
                      }
                      @if (p.pass_status === 'Approved' || p.pass_status === 'Active') {
                        <button class="tla-btn scan" (click)="openScan(p)" title="Gate Scan"><i class="bi bi-qr-code-scan"></i></button>
                      }
                      <button class="tla-btn view" (click)="viewPass(p)" title="View Pass"><i class="bi bi-eye-fill"></i></button>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Right: Currently Out + Recent Log -->
      <div class="col-lg-5 d-flex flex-column gap-3">
        <!-- Currently Out -->
        <div class="gp-card">
          <div class="gpc-header danger-header"><i class="bi bi-person-walking me-2"></i>Currently Outside ({{ stats().outNow }})</div>
          <div class="gpc-body p-0">
            @let outNow = passes().filter(p => p.pass_status === 'Active' && p.actual_out_time && !p.actual_in_time);
            @if (outNow.length === 0) {
              <div class="p-4 text-center text-muted small">All students are present inside.</div>
            }
            @for (p of outNow; track p.id) {
              <div class="out-student-row" [class.overdue-row]="isOverdue(p)">
                <div class="osr-avatar">{{ getInitials(p.student_name) }}</div>
                <div>
                  <div class="osr-name">{{ p.student_name }}</div>
                  <div class="osr-meta">{{ dataService.getClassName(p.class_name) }}-{{ p.section }} · Out: {{ formatTime(p.actual_out_time) }}</div>
                  @if (isOverdue(p)) { <div class="overdue-label"><i class="bi bi-exclamation-triangle-fill me-1"></i>Overdue — should return by {{ formatTimeStr(p.in_time) }}</div> }
                </div>
                <div class="ms-auto d-flex gap-1">
                  @if (!p.parent_notified) {
                    <button class="tla-btn notify" (click)="notifyParent(p)" title="Notify Parent"><i class="bi bi-bell-fill"></i></button>
                  } @else {
                    <i class="bi bi-bell-slash text-muted" style="font-size:.78rem;" title="Parent notified"></i>
                  }
                  <button class="tla-btn scan" (click)="openScan(p)" title="Mark IN"><i class="bi bi-qr-code-scan"></i></button>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Recent Log -->
        <div class="gp-card flex-grow-1">
          <div class="gpc-header"><i class="bi bi-journal-text me-2"></i>Recent Gate Activity</div>
          <div class="gpc-body p-0">
            @for (log of gateLogs().slice(0,6); track log.id) {
              <div class="log-mini-row">
                <div class="lmr-event {{ log.io_event === 'OUT' ? 'ev-out' : 'ev-in' }}">
                  <i class="bi {{ log.io_event === 'OUT' ? 'bi-arrow-right' : 'bi-arrow-left' }}"></i>
                </div>
                <div>
                  <div class="lmr-name">{{ log.student_name }} <span class="lmr-class">{{ dataService.getClassName(log.class_name) }}</span></div>
                  <div class="lmr-time">{{ formatTime(log.io_time) }} · {{ log.scanned_by }}</div>
                </div>
                <span class="ms-auto lmr-pass-no">{{ log.pass_no }}</span>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ===================== ISSUE PASS ===================== -->
  @if (activeTab() === 'issue') {
    <div class="row g-4">
      <div class="col-lg-7">
        <div class="gp-card">
          <div class="gpc-header"><i class="bi bi-plus-circle-fill me-2"></i>Issue / Request Gate Pass</div>
          <div class="gpc-body">

            <!-- Student lookup -->
            <div class="student-lookup mb-4">
              <label class="form-label fw-semibold">Student <span class="req">*</span></label>
              <div class="input-group">
                <span class="input-group-text bg-light"><i class="bi bi-search text-muted"></i></span>
                <input type="text" class="form-control"
                  [(ngModel)]="issueForm.search_student"
                  (input)="onStudentSearch()"
                  placeholder="Search by name, student ID, or roll no."
                  autocomplete="off"
                  [class.is-invalid]="issueSubmitted && issueErrors['student']">
              </div>
              @if (issueSubmitted && issueErrors['student']) { <div class="text-danger small mt-1">{{ issueErrors['student'] }}</div> }
              @if (studentSuggestions.length > 0) {
                <div class="student-suggestions">
                  @for (s of studentSuggestions; track s.id) {
                    <div class="ss-item" (click)="selectStudent(s)">
                      <div class="ss-av">{{ getInitials(s.name) }}</div>
                      <div>
                        <div class="ss-name">{{ s.name }}</div>
                        <div class="ss-meta">{{ dataService.getClassName(s.class_name) }}-{{ s.section }} · Roll: {{ s.roll_no }} · {{ s.id }}</div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            @if (issueForm.student_name) {
              <div class="selected-student-card mb-4">
                <div class="ssc-avatar">{{ getInitials(issueForm.student_name!) }}</div>
                <div>
                  <div class="ssc-name">{{ issueForm.student_name }}</div>
                  <div class="ssc-meta">{{ dataService.getClassName(issueForm.class_name!) }}-{{ issueForm.section }} · Roll: {{ issueForm.roll_no }} · ID: {{ issueForm.student_id }}</div>
                  <div class="ssc-mobile">
                    <i class="bi bi-telephone-fill me-1"></i>Parent: {{ issueForm.parent_mobile }}
                  </div>
                </div>
                <button class="ssc-clear" (click)="issueForm.student_id=0; issueForm.student_name=''"><i class="bi bi-x-circle-fill"></i></button>
              </div>
            }

            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label fw-semibold">Pass Type <span class="req">*</span></label>
                <select class="form-select" [(ngModel)]="issueForm.pass_type" [class.is-invalid]="issueSubmitted && issueErrors['pass_type']">
                  @for (t of passTypes; track t) { <option [value]="t">{{ t }}</option> }
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Date <span class="req">*</span></label>
                <input type="date" class="form-control" [(ngModel)]="issueForm.reg_date" [class.is-invalid]="issueSubmitted && issueErrors['date']">
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Requested By</label>
                <select class="form-select" [(ngModel)]="issueForm.requested_by">
                  @for (r of requestedByOpts; track r) { <option [value]="r" class="text-capitalize">{{ r }}</option> }
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Out Time <span class="req">*</span></label>
                <input type="time" class="form-control" [(ngModel)]="issueForm.out_time" [class.is-invalid]="issueSubmitted && issueErrors['out_time']">
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold">Return Time</label>
                <input type="time" class="form-control" [(ngModel)]="issueForm.in_time">
                <div class="form-text">Leave blank if not returning today</div>
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Reason <span class="req">*</span></label>
                <textarea class="form-control" [(ngModel)]="issueForm.reason" rows="2" maxlength="300"
                  placeholder="Reason for requesting gate pass…"
                  [class.is-invalid]="issueSubmitted && issueErrors['reason']"></textarea>
                @if (issueSubmitted && issueErrors['reason']) { <div class="invalid-feedback">{{ issueErrors['reason'] }}</div> }
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Contact Person (who picks up)</label>
                <input type="text" class="form-control" [(ngModel)]="issueForm.contact_person" maxlength="80" placeholder="e.g. Ramesh Sharma (Father)">
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Contact Mobile</label>
                <div class="input-group">
                  <span class="input-group-text bg-light">+91</span>
                  <input type="tel" class="form-control" [(ngModel)]="issueForm.contact_mobile" maxlength="10" placeholder="10-digit">
                </div>
              </div>

              <div class="col-12">
                <label class="form-label fw-semibold">Remarks</label>
                <input type="text" class="form-control" [(ngModel)]="issueForm.remarks" maxlength="200" placeholder="Additional notes…">
              </div>
            </div>

            @if (issueSubmitted && hasErrors()) {
              <div class="alert alert-danger d-flex gap-2 align-items-center mt-3 py-2">
                <i class="bi bi-exclamation-triangle-fill"></i>
                Please fix the highlighted errors.
              </div>
            }

            <div class="d-flex gap-3 justify-content-end mt-4">
              <button class="btn btn-outline-secondary px-4" (click)="openIssueForm()">Reset</button>
              <button class="btn btn-outline-primary px-4" (click)="submitIssue(false)">
                <i class="bi bi-send me-2"></i>Submit for Approval
              </button>
              <button class="btn gp-btn-primary px-4" (click)="submitIssue(true)">
                <i class="bi bi-check-circle-fill me-2"></i>Issue & Approve
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Info Panel -->
      <div class="col-lg-5">
        <div class="gp-card">
          <div class="gpc-header"><i class="bi bi-info-circle-fill me-2"></i>Pass Types Reference</div>
          <div class="gpc-body">
            @for (t of passTypes; track t) {
              <div class="type-ref-row">
                <span class="{{ getTypeClass(t) }} type-ref-badge"><i class="bi {{ getTypeIcon(t) }} me-1"></i>{{ t }}</span>
                <span class="type-ref-desc small text-muted">
                  @if (t === 'Outgoing')       { Student leaving school premises for personal/family reasons }
                  @if (t === 'Incoming Late')   { Student arriving after school start time }
                  @if (t === 'Emergency Exit')  { Immediate departure due to family emergency }
                  @if (t === 'Medical')         { Medical appointment or health-related absence }
                  @if (t === 'Event')           { School-approved event/competition outside campus }
                  @if (t === 'Half Day')        { Student leaving after completing half the school day }
                </span>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ===================== REQUESTS ===================== -->
  @if (activeTab() === 'requests') {
    <div class="gp-card mb-3"><div class="gpc-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search</label>
          <div class="input-group"><span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
            <input class="form-control border-start-0" placeholder="Name, pass no., class, reason…"
              [value]="reqSearch()" (input)="reqSearch.set($any($event.target).value)">
            @if (reqSearch()) { <button class="btn btn-outline-secondary" (click)="reqSearch.set('')"><i class="bi bi-x"></i></button> }
          </div></div>
        <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Status</label>
          <select class="form-select" [value]="reqStatus()" (change)="reqStatus.set($any($event.target).value)">
            <option value="">All</option>
            @for (s of ['Pending','Approved','Rejected','Active','Completed','Cancelled']; track s) { <option [value]="s">{{ s }}</option> }
          </select></div>
        <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Type</label>
          <select class="form-select" [value]="reqType()" (change)="reqType.set($any($event.target).value)">
            <option value="">All Types</option>
            @for (t of passTypes; track t) { <option [value]="t">{{ t }}</option> }
          </select></div>
        <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Date</label>
          <input type="date" class="form-control" [value]="reqDate()" (change)="reqDate.set($any($event.target).value)"></div>
        <div class="col-auto">
          <button class="btn btn-outline-secondary" (click)="resetFilters()"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button>
        </div>
      </div>
      <small class="text-muted mt-2 d-block">{{ filteredRequests().length }} passes</small>
    </div></div>

    @if (filteredRequests().length === 0) {
      <div class="empty-state"><i class="bi bi-list-check"></i><p>No passes found</p></div>
    } @else {
      <div class="passes-list">
        @for (p of filteredRequests(); track p.id) {
          <div class="pass-card" [class.pc-overdue]="isOverdue(p)" [class.pc-pending]="p.pass_status === 'Pending'">
            <div class="pc-left">
              <div class="pc-type-icon {{ getTypeClass(p.pass_type) }}">
                <i class="bi {{ getTypeIcon(p.pass_type) }}"></i>
              </div>
            </div>
            <div class="pc-main">
              <div class="pc-row1">
                <span class="pc-pass-no">{{ p.pass_no }}</span>
                <span class="{{ getTypeClass(p.pass_type) }} pc-type-tag">{{ p.pass_type }}</span>
                <span class="{{ getStatusClass(p.pass_status) }} ms-1">
                  <i class="bi {{ getStatusIcon(p.pass_status) }} me-1"></i>{{ p.pass_status }}
                </span>
                @if (isOverdue(p)) { <span class="overdue-tag"><i class="bi bi-alarm-fill me-1"></i>OVERDUE</span> }
              </div>
              <div class="pc-student">{{ p.student_name }} · {{ dataService.getClassName(p.class_name) }}-{{ p.section }} · {{ p.roll_no }}</div>
              <div class="pc-reason">{{ p.reason }}</div>
              <div class="pc-meta">
                <span><i class="bi bi-calendar3 me-1"></i>{{ formatDate(p.reg_date) }}</span>
                <span><i class="bi bi-clock me-1 ms-2"></i>Out: {{ formatTimeStr(p.out_time) }}{{ p.in_time ? ' | Return: ' + formatTimeStr(p.in_time) : '' }}</span>
                @if (p.contact_person) { <span><i class="bi bi-person me-1 ms-2"></i>{{ p.contact_person }}</span> }
                <span class="ms-2 text-capitalize text-muted small">Req. by {{ p.requested_by }}</span>
              </div>
              @if (p.rejected_reason) {
                <div class="pc-rejected-reason"><i class="bi bi-x-circle me-1 text-danger"></i>{{ p.rejected_reason }}</div>
              }
              @if (p.actual_out_time) {
                <div class="pc-timestamps">
                  <span class="ts-out"><i class="bi bi-arrow-right-circle me-1"></i>OUT: {{ formatTime(p.actual_out_time) }}</span>
                  @if (p.actual_in_time) { <span class="ts-in ms-3"><i class="bi bi-arrow-left-circle me-1"></i>IN: {{ formatTime(p.actual_in_time) }}</span> }
                  @if (p.actual_out_time && p.actual_in_time) { <span class="ts-dur ms-3">{{ calcDuration(p.actual_out_time, p.actual_in_time) }}</span> }
                </div>
              }
            </div>
            <div class="pc-actions">
              @if (p.pass_status === 'Pending') {
                <button class="pca-btn pca-approve" (click)="openApprove(p)" title="Approve">
                  <i class="bi bi-check-circle-fill me-1"></i>Approve
                </button>
                <button class="pca-btn pca-reject"  (click)="openReject(p)"  title="Reject">
                  <i class="bi bi-x-circle-fill me-1"></i>Reject
                </button>
              }
              @if (p.pass_status === 'Approved') {
                <button class="pca-btn pca-scan" (click)="openScan(p)" title="Gate Scan">
                  <i class="bi bi-qr-code-scan me-1"></i>Gate Scan
                </button>
              }
              @if (p.pass_status === 'Active') {
                <button class="pca-btn pca-scan" (click)="openScan(p)" title="Mark Return">
                  <i class="bi bi-qr-code-scan me-1"></i>Mark Return
                </button>
                @if (!p.parent_notified) {
                  <button class="pca-btn pca-notify" (click)="notifyParent(p)" title="Notify Parent">
                    <i class="bi bi-bell-fill me-1"></i>Notify
                  </button>
                }
              }
              <button class="pca-btn pca-view" (click)="viewPass(p)" title="View / Print">
                <i class="bi bi-eye-fill me-1"></i>View
              </button>
              @if (p.pass_status === 'Pending' || p.pass_status === 'Approved') {
                <button class="pca-btn pca-cancel" (click)="cancelPass(p.id)" title="Cancel">
                  <i class="bi bi-slash-circle-fill me-1"></i>Cancel
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ===================== ACTIVE PASSES ===================== -->
  @if (activeTab() === 'active') {
    <div class="gp-card mb-3"><div class="gpc-body">
      <div class="input-group" style="max-width:400px">
        <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
        <input class="form-control border-start-0" placeholder="Search active passes…"
          [value]="activeSearch()" (input)="activeSearch.set($any($event.target).value)">
      </div>
      <small class="text-muted mt-2 d-block">{{ activePasses().length }} active pass(es)</small>
    </div></div>

    @if (activePasses().length === 0) {
      <div class="empty-state"><i class="bi bi-record-circle"></i><p>No active passes right now</p></div>
    } @else {
      <div class="active-passes-grid">
        @for (p of activePasses(); track p.id) {
          <div class="active-pass-card" [class.apc-overdue]="isOverdue(p)">
            <div class="apc-header">
              <span class="{{ getTypeClass(p.pass_type) }} type-badge"><i class="bi {{ getTypeIcon(p.pass_type) }} me-1"></i>{{ p.pass_type }}</span>
              <span class="ms-auto apc-pass-no">{{ p.pass_no }}</span>
              @if (isOverdue(p)) { <span class="overdue-tag ms-1"><i class="bi bi-alarm-fill me-1"></i>OVERDUE</span> }
            </div>
            <div class="apc-student-row">
              <div class="apc-avatar">{{ getInitials(p.student_name) }}</div>
              <div>
                <div class="apc-name">{{ p.student_name }}</div>
                <div class="apc-class">{{ dataService.getClassName(p.class_name) }}-{{ p.section }} · {{ p.roll_no }}</div>
              </div>
            </div>
            <div class="apc-times">
              @if (p.actual_out_time) {
                <div class="apc-time out-time"><i class="bi bi-arrow-right-circle-fill me-1"></i>Out at {{ formatTime(p.actual_out_time) }}</div>
              }
              @if (p.in_time) {
                <div class="apc-time" [class.return-overdue]="isOverdue(p)" [class.return-time]="!isOverdue(p)">
                  <i class="bi bi-alarm me-1"></i>Return by {{ formatTimeStr(p.in_time) }}
                </div>
              }
            </div>
            <div class="apc-reason">{{ p.reason }}</div>
            <div class="apc-footer">
              <button class="apf-btn scan" (click)="openScan(p)"><i class="bi bi-qr-code-scan me-1"></i>Mark Return</button>
              @if (!p.parent_notified) {
                <button class="apf-btn notify" (click)="notifyParent(p)"><i class="bi bi-bell-fill me-1"></i>Notify</button>
              } @else {
                <span class="notified-chip"><i class="bi bi-bell-slash me-1"></i>Notified</span>
              }
              <button class="apf-btn view" (click)="viewPass(p)"><i class="bi bi-eye-fill"></i></button>
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- ===================== GATE LOG ===================== -->
  @if (activeTab() === 'log') {
    <div class="gp-card mb-3"><div class="gpc-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-5"><label class="form-label small fw-semibold text-muted">Search</label>
          <div class="input-group"><span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
            <input class="form-control border-start-0" placeholder="Student, pass no., class…"
              [value]="logSearch()" (input)="logSearch.set($any($event.target).value)">
            @if (logSearch()) { <button class="btn btn-outline-secondary" (click)="logSearch.set('')"><i class="bi bi-x"></i></button> }
          </div></div>
        <div class="col-md-3"><label class="form-label small fw-semibold text-muted">Date</label>
          <input type="date" class="form-control" [value]="logDate()" (change)="logDate.set($any($event.target).value)"></div>
        <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Event</label>
          <select class="form-select" [value]="logEvent()" (change)="logEvent.set($any($event.target).value)">
            <option value="">All</option><option value="OUT">Exit (OUT)</option><option value="IN">Entry (IN)</option>
          </select></div>
        <div class="col-auto"><button class="btn btn-outline-secondary" (click)="logSearch.set(''); logDate.set(''); logEvent.set('')"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button></div>
      </div>
      <small class="text-muted mt-2 d-block">{{ filteredLogs().length }} log entries</small>
    </div></div>

    <div class="gp-card"><div class="gpc-body p-0">
      @if (filteredLogs().length === 0) {
        <div class="empty-state"><i class="bi bi-journal-x"></i><p>No log entries found</p></div>
      } @else {
        <div class="table-responsive">
          <table class="gp-table">
            <thead><tr>
              <th>Event</th><th>Pass No.</th><th>Student</th><th>Class</th>
              <th>Time</th><th>Scanned By</th><th>Remarks</th>
            </tr></thead>
            <tbody>
              @for (log of filteredLogs(); track log.id) {
                <tr [class.log-out-row]="log.io_event === 'OUT'" [class.log-in-row]="log.io_event === 'IN'">
                  <td>
                    <span class="event-badge {{ log.io_event === 'OUT' ? 'ev-out-badge' : 'ev-in-badge' }}">
                      <i class="bi {{ log.io_event === 'OUT' ? 'bi-arrow-right-circle-fill' : 'bi-arrow-left-circle-fill' }} me-1"></i>
                      {{ log.io_event }}
                    </span>
                  </td>
                  <td class="font-monospace small fw-semibold">{{ log.pass_no }}</td>
                  <td class="fw-semibold small">{{ log.student_name }}</td>
                  <td class="small text-secondary">{{ dataService.getClassName(log.class_name) }}</td>
                  <td class="small">{{ formatDateTime(log.io_time) }}</td>
                  <td class="small text-muted">{{ log.scanned_by }}</td>
                  <td class="small text-muted">{{ log.remarks || '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div></div>
  }

</div>
